import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Skeleton from "@material-ui/lab/Skeleton";
import { useDispatch, useSelector } from "react-redux";
import { appService } from "../App/app.service";
import { rccColors } from "../RevenueCommandCenter/colors";
import { useRccTypography } from "../RevenueCommandCenter/typography";
import BackToRcc from "../RevenueCommandCenter/BackToRcc";

const CALENDAR_ICON_URL =
  "https://skygeniwebapp.blob.core.windows.net/icons/calendar.png";
const NEEDS_ATTENTION_ICON_URL =
  "https://skygeniwebapp.blob.core.windows.net/icons/needs-attention.png";
const WHATS_GOING_WELL_ICON_URL =
  "https://skygeniwebapp.blob.core.windows.net/icons/whats-going-well.png";

const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: "100vh",
    // background: rccColors.neutral[50],
    padding: "32px 60px",
    maxWidth: "1000px",
    margin: "0 auto",
  },
  headerSection: {
    textAlign: "center",
    marginBottom: "32px",
  },
  title: {
    color: rccColors.neutral[900],
    marginBottom: "8px",
  },
  subtitle: {
    color: rccColors.neutral[700],
    maxWidth: "800px",
    margin: "0 auto",
  },
  dateContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "16px",
  },
  dateText: {
    color: rccColors.blue[800],
  },
  sectionContainer: {
    // margintop: "32px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  tableContainer: {
    borderRadius: 0,
    border: "1px solid #D3D3D3",
    overflow: "hidden",
    backgroundColor: rccColors.materialGrey[0],
  },
  tableHeader: {
    backgroundColor: "#3272CA",
  },
  tableHeaderCell: {
    color: rccColors.materialGrey[0],
    padding: "8px",
    border: "none",
    textAlign: "center",
  },
  tableHeaderCellNumber: {
    color: rccColors.materialGrey[0],
    padding: "8px",
    border: "none",
    // width: "60px",
    textAlign: "center",
    borderRight: "1px solid rgba(255, 255, 255, 0.35)",
  },
  tableRow: {
    backgroundColor: rccColors.materialGrey[0],
    "&:nth-of-type(even)": {
      backgroundColor: rccColors.materialGrey[100], // #F5F5F5
    },
    "&:last-child td": {
      borderBottom: "none",
    },
  },
  tableCell: {
    padding: "8px",
    borderBottom: "1px solid #D3D3D3",
    color: rccColors.neutral[900],
  },
  tableCellNumber: {
    padding: "8px",
    borderBottom: "1px solid #D3D3D3",
    color: rccColors.neutral[900],
    // width: "60px",
    textAlign: "right",
    borderRight: "1px solid #D3D3D3",
  },
  errorState: {
    color: rccColors.red[600],
    textAlign: "center",
    padding: "40px",
  },
  emptyState: {
    color: rccColors.neutral[500],
    textAlign: "center",
    padding: "20px",
    fontStyle: "italic",
  },
  loadingContainer: {
    padding: "40px",
  },
  contentCard: {
    backgroundColor: rccColors.materialGrey[0],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
}));

function PerformanceAlerts() {
  const classes = useStyles();
  const typography = useRccTypography();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.app?.user);
  const settings = useSelector((state) => state.app?.settings);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchingRef = useRef(false);
  const settingsFetchRef = useRef(false);

  // Get currentFiscalQuarter from user
  const fiscalQuarter = user?.currentFiscalQuarter
    ? `${user.currentFiscalQuarter} QTD`
    : "Current Fiscal Quarter";

  useEffect(() => {
    if (fetchingRef.current) return;

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    // Match Settings -> PlatformInfo behavior: fetch settings and render state.app.settings.lastPull.
    // Note: state.app.settings is initialized as { lastPull: "", nextPull: "" }, so check lastPull specifically.
    if ((!settings?.lastPull || settings === "") && !settingsFetchRef.current) {
      settingsFetchRef.current = true;
      dispatch({ type: "get_settings_request" });
      appService.getSettings().then(
        (json) => dispatch({ type: "get_settings_success", json }),
        (err) => {
          dispatch({
            type: "get_settings_failure",
            error: typeof err === "object" ? "Something went wrong" : err,
          });
        },
      );
    }

    appService.getPerformanceInsights().then(
      (response) => {
        fetchingRef.current = false;
        if (response.success && response.data) {
          setData(response.data);
        } else {
          setError("Failed to load performance insights");
        }
        setLoading(false);
      },
      (err) => {
        fetchingRef.current = false;
        setError(err?.message || "Failed to load performance insights");
        setLoading(false);
      },
    );
  }, []);

  // Render loading state
  if (loading) {
    return (
      <Box className={classes.container}>
        <Box className={classes.loadingContainer}>
          <Skeleton
            variant="text"
            width="40%"
            height={48}
            style={{ margin: "0 auto 16px" }}
          />
          <Skeleton
            variant="text"
            width="60%"
            height={24}
            style={{ margin: "0 auto 32px" }}
          />
          <Skeleton
            variant="rect"
            width="100%"
            height={300}
            style={{ borderRadius: "8px", marginBottom: "24px" }}
          />
          <Skeleton
            variant="rect"
            width="100%"
            height={300}
            style={{ borderRadius: "8px" }}
          />
        </Box>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box className={classes.container}>
        <Box className={classes.headerSection}>
          <BackToRcc />
          <Typography
            className={`${typography.displaySmallBold} ${classes.title}`}
          >
            Performance Alerts
          </Typography>
        </Box>
        <Typography className={classes.errorState}>{error}</Typography>
      </Box>
    );
  }

  // Extract data categories (supports both legacy and new DC-backed shapes)
  const normalizeInsightItems = (value, sourceKey) => {
    if (!Array.isArray(value)) return [];

    return value
      .map((item, idx) => {
        if (typeof item === "string") {
          return {
            id: `${sourceKey}-${idx}`,
            Check_Id: null,
            Diagnostic_Summary: item,
            Diagnostic_Summary_Verbose: null,
          };
        }
        if (!item || typeof item !== "object") return null;

        const diagnosticSummary =
          typeof item.Diagnostic_Summary === "string"
            ? item.Diagnostic_Summary
            : typeof item.text === "string"
              ? item.text
              : null;

        if (!diagnosticSummary) return null;

        return {
          ...item,
          id: item.id ?? `${sourceKey}-${idx}`,
          Check_Id: item.Check_Id ?? null,
          Diagnostic_Summary: diagnosticSummary,
          Diagnostic_Summary_Verbose:
            typeof item.Diagnostic_Summary_Verbose === "string"
              ? item.Diagnostic_Summary_Verbose
              : null,
        };
      })
      .filter(Boolean);
  };

  const mergeInsights = (primaryKey, legacyKey) => {
    const primary = normalizeInsightItems(data?.[primaryKey], primaryKey);
    const legacy = normalizeInsightItems(data?.[legacyKey], legacyKey);
    return primary.length > 0 ? primary : legacy;
  };

  const needsAttention = mergeInsights(
    "WORSE_THAN_EXPECTED",
    "Needs Attention",
  );
  const whatsGoingWell = mergeInsights(
    "BETTER_THAN_EXPECTED",
    "What's Going Well",
  );

  // Format date to "7 Jan 2026" format
  // lastPull is already formatted as "10:30 AM EST, Monday, Jan. 07, 2026"
  // We extract the date part (last two comma-separated parts) and reformat
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      // Extract date part - format is "time tz, weekday, month. day, year"
      // We need the last two parts: "month. day" and "year"
      const parts = dateString.split(",");
      if (parts.length >= 3) {
        const monthDayPart = parts[parts.length - 2].trim(); // "Jan. 07"
        const yearPart = parts[parts.length - 1].trim(); // "2026"

        // Parse "Jan. 07" to extract month and day
        const monthDayMatch = monthDayPart.match(/(\w+)\.?\s+0?(\d+)/);
        if (monthDayMatch) {
          const month = monthDayMatch[1]; // "Jan"
          const day = parseInt(monthDayMatch[2], 10); // 7 (removes leading zero)

          return `${day} ${month} ${yearPart}`; // "7 Jan 2026"
        }
      }
      return dateString; // Return original if format is unexpected
    } catch (error) {
      return dateString; // Return original if parsing fails
    }
  };

  // Render table section
  const renderTable = (items) => {
    if (!items || items.length === 0) return null;

    return (
      <TableContainer className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell
                className={`${typography.textExtraSmallBold} ${classes.tableHeaderCellNumber}`}
                align="center"
              >
                #
              </TableCell>
              <TableCell
                className={`${typography.textExtraSmallBold} ${classes.tableHeaderCell}`}
              >
                Description
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={item.id || index} className={classes.tableRow}>
                <TableCell
                  className={`${typography.textSmallRegular} ${classes.tableCellNumber}`}
                  align="center"
                >
                  {index + 1}
                </TableCell>
                <TableCell
                  className={`${typography.textSmallRegular} ${classes.tableCell}`}
                >
                  {item.Diagnostic_Summary}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render nested data table as JSX table for email parsing
  // Accept 'headerColor' as a second argument
  // We default to blue (#1977D3) if no color is provided
  const renderDataTable = (items, headerColor = "#1977D3") => {
    if (!items || items.length === 0) return null;

    return (
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          border: "1px solid #D3D3D3",
          backgroundColor: "#FFFFFF",
          borderRadius: 0,
        }}
        cellPadding="0"
        cellSpacing="0"
      >
        <thead>
          <tr style={{ backgroundColor: headerColor }}>
            <th
              style={{
                color: "#FFFFFF",
                padding: "8px",
                border: "none",
                textAlign: "center",
                borderRight: "1px solid rgba(255, 255, 255, 0.35)",
                fontFamily: '"Onest", sans-serif',
                fontSize: "0.75rem",
                lineHeight: "1.125rem",
                fontWeight: 700,
              }}
            >
              #
            </th>
            <th
              style={{
                color: "#FFFFFF",
                padding: "8px",
                border: "none",
                textAlign: "center",
                fontFamily: '"Onest", sans-serif',
                fontSize: "0.75rem",
                lineHeight: "1.125rem",
                fontWeight: 700,
              }}
            >
              Description
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const bgColor = index % 2 === 0 ? "#FFFFFF" : "#F5F5F5";
            const isLastRow = index === items.length - 1;
            return (
              <tr key={item.id || index} style={{ backgroundColor: bgColor }}>
                <td
                  style={{
                    padding: "8px",
                    borderBottom: isLastRow ? "none" : "1px solid #D3D3D3",
                    color: "#263238",
                    textAlign: "center",
                    borderRight: "1px solid #D3D3D3",
                    fontFamily: '"Onest", sans-serif',
                    fontSize: "0.875rem",
                    lineHeight: "1.25rem",
                    fontWeight: 400,
                  }}
                >
                  {index + 1}
                </td>
                <td
                  style={{
                    padding: "8px",
                    borderBottom: isLastRow ? "none" : "1px solid #D3D3D3",
                    color: "#263238",
                    fontFamily: '"Onest", sans-serif',
                    fontSize: "0.875rem",
                    lineHeight: "1.25rem",
                    fontWeight: 400,
                  }}
                >
                  {item.Diagnostic_Summary || ""}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <Box className={classes.container}>
      <BackToRcc />
      {/* Header Section */}
      <Box className={classes.headerSection}>
        <Typography
          className={`${typography.displayMediumSemibold} ${classes.title}`}
        >
          Performance Alerts
        </Typography>
        <Typography
          className={`${typography.textMediumRegular} ${classes.subtitle}`}
        >
          Proactive notifications highlighting areas that are performing outside
          expected benchmarks, so teams can act early.
        </Typography>
      </Box>

      <Paper className={classes.contentCard} elevation={0}>
        <table
          id="performance-alerts-parent-table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
          }}
          cellPadding="0"
          cellSpacing="0"
        >
          <tbody>
            {/* Date/Quarter Indicator */}
            <tr>
              <td
                style={{
                  padding: "0 0 16px 0",
                  fontFamily: '"Onest", sans-serif',
                  fontSize: "1.125rem",
                  lineHeight: "1.75rem",
                  fontWeight: 600,
                  color: rccColors.blue[800],
                }}
              >
                <table
                  cellPadding="0"
                  cellSpacing="0"
                  style={{ borderCollapse: "collapse" }}
                >
                  <tbody>
                    <tr>
                      <td style={{ padding: "0 8px 0 0" }}>
                        <img
                          src={CALENDAR_ICON_URL}
                          alt="Calendar"
                          width={16}
                          height={18}
                          style={{
                            display: "block",
                            width: 16,
                            height: 18,
                            objectFit: "contain",
                            verticalAlign: "middle",
                          }}
                        />
                      </td>
                      <td style={{ verticalAlign: "middle" }}>
                        {fiscalQuarter}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>

            {/* Subheading */}
            <tr>
              <td
                style={{
                  padding: "0 0 24px 0",
                  fontFamily: '"Onest", sans-serif',
                  fontSize: "1.5rem",
                  lineHeight: "2rem",
                  fontWeight: 600,
                  color: rccColors.neutral[900],
                }}
              >
                Performance Alerts as of {formatDate(settings?.lastPull)}
              </td>
            </tr>

            <>
              {/* What's Going Well Section - only when there are items */}
              {whatsGoingWell.length > 0 && (
                <tr>
                  <td style={{ padding: "0 0 24px 0" }}>
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                      cellPadding="0"
                      cellSpacing="0"
                    >
                      <tbody>
                        <tr>
                          <td
                            style={{
                              padding: "0 0 8px 0",
                              fontFamily: '"Onest", sans-serif',
                              fontSize: "1.125rem",
                              lineHeight: "1.75rem",
                              fontWeight: 500,
                              color: rccColors.green[700],
                            }}
                          >
                            <table
                              cellPadding="0"
                              cellSpacing="0"
                              style={{ borderCollapse: "collapse" }}
                            >
                              <tbody>
                                <tr>
                                  <td style={{ padding: "0 8px 0 0" }}>
                                    <img
                                      src={WHATS_GOING_WELL_ICON_URL}
                                      alt="What's going well"
                                      width={20}
                                      height={20}
                                      style={{
                                        display: "block",
                                        width: 20,
                                        height: 20,
                                        objectFit: "contain",
                                        verticalAlign: "middle",
                                      }}
                                    />
                                  </td>
                                  <td style={{ verticalAlign: "middle" }}>
                                    What's Going Well
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: 0 }}>
                            {renderDataTable(whatsGoingWell)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}

              {/* Needs Attention Section - only when there are items */}
              {needsAttention.length > 0 && (
                <tr>
                  <td style={{ padding: 0 }}>
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                      cellPadding="0"
                      cellSpacing="0"
                    >
                      <tbody>
                        <tr>
                          <td
                            style={{
                              padding: "0 0 8px 0",
                              fontFamily: '"Onest", sans-serif',
                              fontSize: "1.125rem",
                              lineHeight: "1.75rem",
                              fontWeight: 500,
                              color: rccColors.orange[800],
                            }}
                          >
                            <table
                              cellPadding="0"
                              cellSpacing="0"
                              style={{ borderCollapse: "collapse" }}
                            >
                              <tbody>
                                <tr>
                                  <td style={{ padding: "0 8px 0 0" }}>
                                    <img
                                      src={NEEDS_ATTENTION_ICON_URL}
                                      alt="Needs attention"
                                      width={20}
                                      height={20}
                                      style={{
                                        display: "block",
                                        width: 20,
                                        height: 20,
                                        objectFit: "contain",
                                        verticalAlign: "middle",
                                      }}
                                    />
                                  </td>
                                  <td style={{ verticalAlign: "middle" }}>
                                    Needs Attention
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: 0 }}>
                            {renderDataTable(needsAttention)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
            </>
          </tbody>
        </table>
      </Paper>
    </Box>
  );
}

export default PerformanceAlerts;
